import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import clsx from 'clsx';

import { Utils } from '@helpers/utils';
import { mutateOnboardingCustomData } from '@hooks/apiHooks';
import { useDecodedParams } from '@hooks/useDecodedParams';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { snackbarSlice } from '@store/slices/snackbar';

import styles from './CandidateInfo.module.scss';
interface OnboardingCustomDataModalProps {
  customData: any | null;
  isActive: boolean;
  closeModal: () => void;
}

const OnboardingCustomDataEditModal: React.FC<OnboardingCustomDataModalProps> = ({
  customData,
  isActive,
  closeModal,
}) => {
  const [editObject, setEditObject] = useState<{ name: string; object: any } | null>(null);
  const [newCustomData, setNewCustomData] = useState(customData);

  const { clientOrganizationId, userId, applicationId, configurationId } = useDecodedParams();
  const { t } = useTranslation(['recruiter', 'integration-state-faq']);
  const dispatch = useDispatch();

  const { mutate: updateCustomData, isPending: loading } = mutateOnboardingCustomData(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
    (response: any) => {
      setNewCustomData(response);
      setEditObject(null);
      dispatch(
        snackbarSlice.actions.showSuccess({
          message: `Onboaridng.CustomData state updated successfully`,
        }),
      );
    },
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const updatedCustomData = { ...newCustomData };
    updatedCustomData[name] = value;
    setNewCustomData(updatedCustomData);
  };

  const handleObjectChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    let parsedValue = value;
    try {
      parsedValue = JSON.parse(value);
    } catch (err) {
      Utils.consoleLog('Invalid JSON');
      return;
    }
    setEditObject({ name: name, object: parsedValue });
  };

  const saveObjectChanges = async () => {
    if (!editObject) return;
    const { object } = editObject;
    setNewCustomData(object);
    setEditObject(null);
  };

  const handleRemoveProperty = (key: string) => {
    const updatedCustomData = { ...newCustomData };
    updatedCustomData[key] = 'remove';
    setNewCustomData(updatedCustomData);
  };

  const handleSubmitState = async () => {
    await updateCustomData(newCustomData);
  };
  return (
    <Dialog open={isActive} onClose={closeModal}>
      <Box>
        <DialogTitle>
          <Box display="flex" flexWrap="wrap" width="100%">
            <p className="tag-ds  h4">{t('recruiter:GENERAL.CANDIDATE_DETAILS.onboardingCustomDataEdit')}</p>
            <div className="tag-ds" style={{ marginLeft: 'auto' }}>
              <button onClick={() => setEditObject({ name: 'customData', object: newCustomData })}>
                <span className="material-icons">settings</span>
              </button>
            </div>
          </Box>
        </DialogTitle>
        <DialogContent>
          {Object.keys(newCustomData || {})
            .filter((k) => newCustomData[k] !== 'remove')
            .map((key, index) => {
              let newCustomDataProp = newCustomData?.[key];
              try {
                newCustomDataProp = JSON.parse(newCustomDataProp);
              } catch (err) {
                // nothing
              }
              const isObject = typeof newCustomDataProp === 'object' && newCustomDataProp !== null;
              return (
                <Box display="flex" flexWrap="wrap" key={index} mb={1} width="100%" flexDirection="column">
                  {isObject ? (
                    <div className={clsx('tag-ds')}>
                      <label>"{key}"</label>
                      <div className={styles.integrationState__buttons}>
                        <button onClick={() => setEditObject({ name: key, object: newCustomDataProp })}>Edit</button>
                        <button onClick={() => handleRemoveProperty(key)}>
                          <span className="material-icons">delete</span>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Box key={index} width="100%" display="flex" flexDirection="column" mb={1}>
                      <div className="tag-ds input-wrapper" style={{ marginBottom: '10px' }}>
                        <label>"{key}"</label>
                        <input
                          value={newCustomDataProp?.toString()}
                          type="text"
                          name={key}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div
                        className={clsx('tag-ds', styles.integrationState__buttons)}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          width: '100%',
                          gap: '10px',
                          marginRight: 'auto',
                        }}
                      >
                        <button onClick={() => handleRemoveProperty(key)}>
                          <span className="material-icons">delete</span>
                          Remove
                        </button>
                      </div>
                    </Box>
                  )}
                </Box>
              );
            })}
        </DialogContent>
        <DialogActions>
          <div className="tag-ds ">
            <button onClick={handleSubmitState} disabled={loading}>
              Save Changes
            </button>
          </div>
        </DialogActions>
        {editObject && (
          <Dialog open={!!editObject} onClose={() => setEditObject(null)}>
            <DialogTitle>
              <Box>
                <Box onClick={() => setEditObject(null)}>
                  <CloseIcon />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              {editObject && (
                <Box my={1}>
                  <Typography variant="h5">{editObject.name || ''}</Typography>
                  <textarea
                    className="integration-state-prop--active"
                    value={JSON.stringify(editObject.object, null, 2)}
                    name={editObject.name}
                    onChange={handleObjectChange}
                    rows={40}
                    cols={100}
                    style={{ width: '100%' }}
                  />
                  <Typography variant="caption">
                    This input field only accepts valid JSON, please edit the JSON in an external editor and paste it
                    here.
                    <br />
                    <span style={{ color: 'red' }}>
                      Please be careful when editing the customData state, as it can affect the integration process.
                    </span>
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <div className="tag-ds ">
                <button onClick={saveObjectChanges}>Save Changes</button>
              </div>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Dialog>
  );
};

export default OnboardingCustomDataEditModal;
